import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StatCard from './StatCard';
import type { CodeCount, DaySeriesPoint, RangeTotals, RepairSummaryRow, ToolRow } from '@/lib/writer/telemetryTypes';

/** Tools exposed to the agent. Anything here with zero calls is dead prompt surface. */
export const KNOWN_TOOLS = [
  'search_literature', 'resolve_citation', 'add_to_bib', 'plot_from_data', 'generate_diagram',
  'table_from_data', 'calculate', 'derive', 'check_math', 'check_claims', 'format_tex',
  'insert_figure', 'image_to_tikz', 'remember', 'paper_map', 'list_sections', 'read_section',
  'read_bib_entry', 'patch_section', 'cite_in_section', 'replace_section', 'create_section',
  'set_preamble', 'validate_document', 'materialize', 'compile', 'apply_template',
];

interface Props {
  series: DaySeriesPoint[];
  totals: RangeTotals;
  tools: ToolRow[];
  repairs: RepairSummaryRow | null;
  repairUnresolved: CodeCount[];
}

const AgentPanel: React.FC<Props> = ({ series, totals, tools, repairs, repairUnresolved }) => {
  const chartData = series.map((p) => ({
    day: p.day.slice(5),
    toolCalls: p.turns ? Number((p.toolCalls / p.turns).toFixed(2)) : 0,
    iterations: p.turns ? Number((p.iterations / p.turns).toFixed(2)) : 0,
    seconds: p.turns ? Number((p.durationMs / p.turns / 1000).toFixed(1)) : 0,
  }));

  const used = new Set(tools.map((t) => t.tool));
  const unused = KNOWN_TOOLS.filter((t) => !used.has(t));
  const maxCount = tools[0]?.count ?? 1;
  const silentTurns = Math.max(0, totals.turns - (tools.find((t) => t.tool === 'patch_section')?.turns ?? 0));
  const l1HitRate = repairs && repairs.layer1_attempts > 0
    ? repairs.layer1_applied / repairs.layer1_attempts
    : null;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Agent behaviour</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Tool calls / turn" value={totals.toolCallsPerTurn.toFixed(2)} />
        <StatCard label="Iterations / turn" value={totals.iterationsPerTurn.toFixed(2)} />
        <StatCard label="Duration / turn" value={`${totals.secondsPerTurn.toFixed(1)}s`} />
        <StatCard
          label="Turns without a section edit"
          tone={totals.turns > 0 && silentTurns / totals.turns > 0.4 ? 'warn' : 'ok'}
          value={`${silentTurns}`}
          hint={totals.turns > 0 ? `${((silentTurns / totals.turns) * 100).toFixed(0)}% of turns` : '—'}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Effort per turn, per day</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={36} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="toolCalls" stroke="#4f46e5" dot={false} name="tool calls" />
                <Line type="monotone" dataKey="iterations" stroke="#06b6d4" dot={false} name="iterations" />
                <Line type="monotone" dataKey="seconds" stroke="#f59e0b" dot={false} name="seconds" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Tool leaderboard</CardTitle>
          </CardHeader>
          <CardContent>
            {tools.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tool calls in this range.</p>
            ) : (
              <ul className="space-y-1.5">
                {tools.map((t) => (
                  <li key={t.tool} className="text-sm">
                    <div className="flex justify-between font-mono">
                      <span>{t.tool}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {t.count} · {totals.turns ? ((t.turns / totals.turns) * 100).toFixed(0) : 0}% of turns
                      </span>
                    </div>
                    <div className="h-1 bg-muted rounded mt-1">
                      <div className="h-1 bg-primary rounded" style={{ width: `${(t.count / maxCount) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {unused.length > 0 && (
              <p className="text-xs text-muted-foreground mt-4">
                <span className="font-medium">Never called in this range:</span>{' '}
                <span className="font-mono">{unused.join(', ')}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Per-tool error rates are not recorded in the ledger today — only the tool names of a
              turn are stored.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Compile repair pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            {!repairs || repairs.attempts === 0 ? (
              <p className="text-sm text-muted-foreground">No auto-repair activity in this range.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <StatCard label="Layer 0 fixes (free)" value={`${repairs.layer0_attempts}`} />
                  <StatCard
                    label="Layer 1 applied"
                    value={`${repairs.layer1_applied} / ${repairs.layer1_attempts}`}
                    hint={l1HitRate === null ? undefined : `${(l1HitRate * 100).toFixed(0)}% hit rate`}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Layer 1 tokens: {(repairs.layer1_tokens_in + repairs.layer1_tokens_out).toLocaleString()}
                </p>
              </>
            )}
            {repairUnresolved.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-1">Unresolved by Layer 1</p>
                <ul className="text-sm font-mono">
                  {repairUnresolved.slice(0, 8).map((c) => (
                    <li key={c.code} className="flex justify-between border-b border-border/40 py-1">
                      <span>{c.code}</span>
                      <span className="text-muted-foreground tabular-nums">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default AgentPanel;
